
import AppNotification from "@/components/common/Notification.vue";
import AppNotificationJobList from "@/components/common/NotificationJobList.vue";
import { JobService } from "@/services/job-service";
import Vue from "vue";
import { Notification } from "../models/notifications/notification";
import { Ticket } from "../models/tickets/ticket";
import { NotificationService } from "../services/notification-service";
import { TicketService } from "../services/ticket-service";
import AppDownloadReportModal from "@/components/reportengine/DownloadReportModal.vue";
import { ISelectItem } from "winfakt-vue-components";
import { formatNotificationTime, getNotificationGroup } from "@/utils/notification-time";

type NotificationFilter = "all" | "unread" | "reminders";

interface IListEntry {
	date: Date;
	ticket?: Ticket;
	notification?: Notification;
}

interface IGroupItem {
	key: string;
	ticket?: Ticket;
	notifications: Notification[];
}

interface IGroup {
	key: string;
	label: string;
	items: IGroupItem[];
}

export default Vue.extend({
	data() {
		return {
			activeJobs: JobService.getJobs(),
			unreadTickets: TicketService.unreadTickets,
			regularNotifications: [] as Notification[],
			fetching: false,
			hasMoreNotifications: true,
			tab: 0,
			filter: "all" as NotificationFilter,
			expanded: {} as {[key: string]: boolean},
			downloadReportItem: null as null | Notification
		};
	},
	computed: {
		filterItems(): ISelectItem<NotificationFilter>[] {
			return [
				{text: this.$ct("filter-all").toString(), value: "all"},
				{text: this.$ct("filter-unread").toString(), value: "unread"},
				{text: this.$ct("filter-reminders").toString(), value: "reminders"},
			];
		},
		unreadCount(): number {
			return this.unreadTickets.length + this.regularNotifications.filter(n => !n.IsRead).length;
		},
		readCount(): number {
			return this.regularNotifications.filter(n => n.IsRead).length;
		},
		totalCount(): number {
			return this.unreadTickets.length + this.regularNotifications.length;
		},
		entries(): IListEntry[] {
			let entries: IListEntry[] = [];
			if (this.filter != "reminders") {
				entries.push(...this.unreadTickets.map(ticket => ({date: ticket.timeLastMessage, ticket})));
			}
			for (let notification of this.regularNotifications) {
				if (this.filter == "unread" && notification.IsRead) continue;
				if (this.filter == "reminders" && notification.Title != "title-calendar-reminder") continue;
				entries.push({date: notification.Date, notification});
			}
			return entries.sort((a, b) => b.date.getTime() - a.date.getTime());
		},
		groups(): IGroup[] {
			let groups: IGroup[] = [];
			let current: IGroup | null = null;
			for (let entry of this.entries) {
				let info = getNotificationGroup(entry.date, this.$i18n.locale);
				if (!current || current.key != info.key) {
					current = {key: info.key, label: this.getGroupLabel(info.key, info.label), items: []};
					groups.push(current);
				}
				if (entry.ticket) {
					current.items.push({key: `ticket-${entry.ticket.id}`, ticket: entry.ticket, notifications: []});
					continue;
				}
				let notification = entry.notification as Notification;
				let key = this.getRepeatKey(notification);
				let last = current.items[current.items.length - 1];
				if (last && !last.ticket && last.key == key) {
					last.notifications.push(notification);
				} else {
					current.items.push({key, notifications: [notification]});
				}
			}
			return groups;
		}
	},
	methods: {
		close() {
			this.$emit("close");
		},
		getGroupLabel(key: string, label: string): string {
			if (key == "today") return this.$ct("group-today").toString();
			if (key == "older") return this.$ct("group-older").toString();
			return label;
		},
		// Identieke meldingen (zelfde titel en boodschap) worden samengevouwen tot één rij met teller
		getRepeatKey(notification: Notification): string {
			if (notification.IsCustom) return `custom-${notification.ID}`;
			if (notification.Type == "error") return `error-${notification.ID}`;
			return `${notification.Title}|${notification.getMessage()}`;
		},
		toggleGroup(key: string) {
			this.$set(this.expanded, key, !this.expanded[key]);
		},
		formatTime(date: Date): string {
			return formatNotificationTime(date, this.$i18n.locale);
		},
		openTicket(ticket: Ticket) {
			this.$router.push(`/support/tickets/${ticket.id}`);
			this.close();
		},
		async markAllNotificationsAsRead() {
			await NotificationService.markNotificationsAsRead();
			this.regularNotifications.forEach(n => n.IsRead = true);
			this.unreadTickets.forEach(t => t.unreadUsers.splice(0, t.unreadUsers.length));
		},
		async deleteAllReadNotifications() {
			await NotificationService.deleteReadNotifications();
			this.regularNotifications = this.regularNotifications.filter(n => !n.IsRead);
		},
		async fetch() {
			if (this.fetching) return;
			if (!this.hasMoreNotifications) return;
			this.fetching = true;
			try {
				let notifications = await NotificationService.getNotifications(this.regularNotifications.length);
				if (notifications.length == 0) {
					this.hasMoreNotifications = false;
				}
				this.regularNotifications.push(...notifications);
				this.fetching = false;
				let notRead = this.regularNotifications.filter(n => !n.IsRead);
				if (notRead.length > 0) {
					await NotificationService.markNotificationsAsRead(notRead);
				}
			} catch (err) {
				this.fetching = false;
			}
		},
		onScroll(event: Event) {
			let div = event.target as HTMLElement;
			let scrollHeight = div.scrollHeight;
			let height = div.clientHeight;
			let offsetHeight = div.offsetHeight;
			if (offsetHeight > (scrollHeight - height)) {
				this.fetch();
			}
		},
		setTab(tab: number) {
			this.tab = tab;
		},
		onNewNotification(notification:Notification){
			this.regularNotifications.unshift(notification);
		}
	},
	async created() {
		NotificationService.on("new-notification", this.onNewNotification);
		await this.fetch();
	},
	destroyed(){
		NotificationService.off("new-notification", this.onNewNotification);
	},
	components: {
		AppNotification,
		AppNotificationJobList,
		AppDownloadReportModal
	},
	i18nEx: {componentPrefix: "views.NotificationsModal"},
});
